import type { RefObject } from "react";
import { useCallback, useState, useEffect } from "react";
import type { SampleViewerModalProps } from "~/components/blocks/product/common/video-legacy-season/sample-viewer-modal";
import { isOpenSampleViewModalVar } from "~/components/stores/detail-product/store";

type UseSampleViewerArgs = {
  modalBodyRef: RefObject<HTMLDivElement>;
  imageDataLength: number;
} & Pick<SampleViewerModalProps, "selectedSampleImageIndex">;

export const useSampleViewer = (args: UseSampleViewerArgs) => {
  const { modalBodyRef, selectedSampleImageIndex, imageDataLength } = args;
  const [pageIndex, setPageIndex] = useState(selectedSampleImageIndex);

  const onNextIndex = useCallback(() => {
    if (pageIndex === imageDataLength - 1) {
      setPageIndex(0);
      return;
    }
    setPageIndex((prev) => prev + 1);
  }, [pageIndex]);

  const onPrevIndex = useCallback(() => {
    if (pageIndex === 0) {
      setPageIndex(imageDataLength - 1);
      return;
    }
    setPageIndex((prev) => prev - 1);
  }, [pageIndex]);

  const keyDownListener = (event: KeyboardEvent) => {
    if (event.key === "ArrowRight") onNextIndex();
    if (event.key === "ArrowLeft") onPrevIndex();
    if (event.key === "Escape") isOpenSampleViewModalVar(false);
  };
  useEffect(() => {
    const scope = modalBodyRef.current;
    if (scope === null) return;
    scope.addEventListener("keydown", keyDownListener, { passive: true });
    return () => {
      scope.removeEventListener("keydown", keyDownListener);
    };
  }, [pageIndex]);

  useEffect(() => {
    const scope = modalBodyRef.current;
    if (scope === null) return;
    const nextButtonElement = scope.querySelector(`button[name="nextButton"]`) as HTMLButtonElement;

    queueMicrotask(() => {
      nextButtonElement.focus();
    });
  }, []);

  return {
    pageIndex,
    onNextIndex,
    onPrevIndex,
  };
};
